/**
 * Card #381. The server-side session lives in Redis under the key
 * `${userId}${email}${merchantId}` (user_management session.service.ts, read back by
 * quotes' BasicAuthGuard and merch-backend's checksession from the `email` HEADER).
 * So the email the browser sends when it CREATES the session (createsession,
 * update-merchant-device-id), the one it sends on every later request (TokenInterceptor),
 * and the one it sends when it REMOVES the session (removeusersession) must be the same
 * string, byte for byte. Every one of those reads goes through sessionEmail() below.
 *
 * The bug: sign-in used to store the session email as '' when "Remember me" was off.
 * The session was created under an empty email, then the interceptor filled the empty
 * value from loginUserValue (since ff722c289) and sent the real email, so no request ever
 * found the session: 401 on the dashboard's first calls and back to the sign-in page.
 *
 * "Remember me" now controls only what it says: whether the sign-in box is pre-filled
 * next time (REMEMBERED_LOGIN_KEY). It no longer changes the session's identity.
 */

/** What the person typed in the sign-in box, kept only when "Remember me" was ticked. */
export const REMEMBERED_LOGIN_KEY = 'osmos_remembered_login';

/**
 * Storage can be blocked (private mode, SecurityError) or full. This helper runs on every
 * request through the interceptor and in the middle of the login handler, so a storage
 * failure is answered with "no value" instead of an exception that would break all traffic
 * or leave sign-in half-written.
 */
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null; // storage unavailable: behave as if the key is absent
  }
}

/**
 * The email the session is keyed by. The stored value first, then the signed-in user's
 * own email: exactly the resolution the interceptor has always used for its header.
 */
export function sessionEmail(): string {
  const stored = readStorage('email');
  if (stored) return stored;
  try {
    const user = JSON.parse(readStorage('loginUserValue') || '{}');
    return user?.email ? `${user.email}` : '';
  } catch (e) {
    return ''; // unparsable loginUserValue: same answer as "nothing stored"
  }
}

/** The part of the /auth/login response user this file reads. */
export interface SignedInUser {
  email?: string | null;
}

/**
 * The session email to store after /auth/login: the account's own email from the login
 * response, never '' and never the typed username. The typed value is only a fallback for
 * a response without an email (user_management accepts the username for that person too).
 */
export function signedInSessionEmail(user: SignedInUser | null | undefined, typedLogin: string): string {
  if (user?.email) return `${user.email}`;
  return typedLogin ? `${typedLogin}` : '';
}

/** The sign-in box pre-fill ("Remember me"), or '' when there is none. */
export function rememberedLogin(): string {
  return readStorage(REMEMBERED_LOGIN_KEY) || '';
}

/**
 * Store the session email after /auth/login (see signedInSessionEmail). Returns false when
 * storage refused the write; sessionEmail() then falls back to the signed-in user.
 */
export function storeSessionEmail(user: SignedInUser | null | undefined, typedLogin: string): boolean {
  try {
    localStorage.setItem('email', signedInSessionEmail(user, typedLogin));
    return true;
  } catch (e) {
    // Error name only: never log the login value.
    console.warn('[session-email] could not store the session email', (e as Error)?.name);
    return false;
  }
}

/**
 * Store or forget the sign-in box pre-fill, per the "Remember me" checkbox. The pre-fill is
 * a convenience: a storage failure here must never stop sign-in, so it is swallowed.
 * Keeping it across sign-out is deliberate: that is what "Remember me" asks for. Unticking
 * the box at the next sign-in removes it.
 */
export function rememberLogin(remember: boolean, typedLogin: string): void {
  try {
    if (remember && typedLogin) {
      localStorage.setItem(REMEMBERED_LOGIN_KEY, `${typedLogin}`);
    } else {
      localStorage.removeItem(REMEMBERED_LOGIN_KEY);
    }
  } catch (e) {
    // Storage unavailable: no pre-fill next time, sign-in carries on. Error name only.
    console.warn('[session-email] could not update the remembered login', (e as Error)?.name);
  }
}
